import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WHATS_NEW_ENTRIES, BADGE_WINDOW_DAYS, SHEET_ENTRY_LIMIT, HOUSE_SPACE_PATH,
} from '../data/whatsNew';
import { newestEntryDate, shouldShowBadge, entryIsUnseen } from '../utils/whatsNewBadge';
import { openBugReport } from '../utils/bugReport';
import { upcomingRows, readSeenEvents, eventDot, markEventsSeen, spacePathFor } from '../utils/exploreUpcoming';

/**
 * "What's new" DATELINE + sheet in the /explore header.
 * v2 recut (Nina 09-15 eve, Option B on the mockup canvas): the chip pill is
 * gone — the trigger is one plain text line under the subtitle showing the
 * NEWEST entry ("NEW  See every room with /room library · more"), and
 * the whole line opens the same popover sheet. On quiet weeks (newest entry
 * older than BADGE_WINDOW_DAYS) the line disappears entirely — zero footprint.
 *
 * The sheet GROUPS entries by scope (`where` in data/whatsNew.js): house
 * entries sit under an "IN THE CLAUDE HOUSE" header that deep-links to the
 * live house, so a command like /room is learned next to the one room it
 * works in. Content = data/whatsNew.js; dot/seen logic = utils/whatsNewBadge.
 *
 * Sits INSIDE the .explore-dark-mode invert wrapper on purpose — plain light
 * colors auto-invert (purple → lavender on dark), same as the marketplace
 * card beside it. ⚠️ No global border-box in this frontend: explicit
 * boxSizing where width pairs with padding.
 */

const SEEN_KEY = 'whats_new_last_seen';

function readLastSeen() {
  try { return window.localStorage.getItem(SEEN_KEY); } catch (e) { return null; }
}
function writeLastSeen(dateStr) {
  try { window.localStorage.setItem(SEEN_KEY, dateStr); } catch (e) { /* private mode etc */ }
}
function localToday() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
function dateLabel(dateStr, today) {
  if (dateStr === today) return 'TODAY';
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return '';
  return `${MONTHS[Number(m[1]) - 1] || ''} ${Number(m[2])}`;
}

function CommandChip({ command, hint }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const copy = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(command).catch(() => {});
      }
    } catch (e) { /* copy is a courtesy, never an error */ }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1400);
  };
  return (
    <span style={styles.chipRow}>
      <button type="button" onClick={copy} style={styles.chip} title="Copy command">
        {command}
      </button>
      <span style={styles.chipHint}>{copied ? 'copied!' : (hint || 'tap to copy')}</span>
    </span>
  );
}

function Entry({ entry, unseen, today, isLast }) {
  return (
    <div
      style={{
        ...styles.entry,
        ...(isLast ? null : styles.entryDivider),
        ...(unseen ? styles.entryUnseen : null),
      }}
    >
      <div style={styles.entryEmoji} aria-hidden="true">{entry.emoji}</div>
      <div style={styles.entryBody}>
        <div style={styles.entryTopRow}>
          <span style={styles.entryTitle}>{entry.title}</span>
          <span style={{ ...styles.entryDate, ...(entry.date === today ? styles.entryDateToday : null) }}>
            {dateLabel(entry.date, today)}
          </span>
        </div>
        <div style={styles.entryText}>{entry.body}</div>
        {entry.command && <CommandChip command={entry.command} hint={entry.commandHint} />}
      </div>
    </div>
  );
}

// 🧭 10-03 explore: one "Coming up at the house" row (utils/exploreUpcoming.upcomingRows).
// The whole row is the door: the house, or the night's room while it runs.
function UpcomingRow({ row, isLast, onGo }) {
  return (
    <button
      type="button"
      onClick={onGo}
      data-ph-capture-attribute-explore-slot="event"
      style={{ ...styles.entry, ...styles.upRow, borderBottom: isLast ? 'none' : styles.entryDivider.borderBottom }}
    >
      <span style={{ ...styles.entryEmoji, ...styles.upEmoji }} aria-hidden="true">{row.glyph}</span>
      <span style={styles.entryBody}>
        <span style={styles.upTitle}>{row.label}</span>
        <span style={{ ...styles.upWhen, ...(row.live ? styles.upWhenLive : null) }}>
          {row.live && <span style={styles.upLiveDot} aria-hidden="true" />}
          {row.when}
          {row.by && <span style={styles.upBy}>{` · ${row.by}`}</span>}
          {row.to && <span style={styles.upTag}>{row.to}</span>}
        </span>
      </span>
    </button>
  );
}
const storageOf = () => { try { return window.localStorage; } catch (e) { return null; } };

// A mouse (not a finger): the only pointer that can hover.
function canHover() {
  try { return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches); } catch (e) { return false; }
}
const HOVER_OPEN_MS = 90; // a pointer passing over the bar never opens it
const HOVER_CLOSE_MS = 220; // time to cross from the trigger to the sheet

// variant="pill" (/explore look C, 09-26): a compact "What's new" pill with a
// dot while unread, the same sheet opening under it from the right edge. The
// host styles the pill through `triggerClassName`. The default dateline is
// unchanged.
// openOnHover (09-30, Nina: it "should open on hover"): with a mouse, resting
// on the trigger opens the sheet and leaving both closes it. A click on a
// hover-opened sheet keeps it open until a second click, Escape or a click
// outside. Touch and keyboard keep click-to-open.
// 🧭 10-03 explore (HOUSE_OCTOBER_UIUX_BATCH_SPEC_2026_10_01 §3.6, R15): `upcoming` = the
// room list's `house_upcoming` (the house row; `houseId` = that row's id). A "Coming up at
// the house" group tops the sheet (up to 3 nights in 14 days, house time); the pill stays
// while nights are coming (a quiet week shows only Coming up + the footer), and its dot also
// lights for a night on its day. The dateline keeps its rule. Absent (the backend flag off)
// = the exact pre-lane DOM (WhatsNewPill.upcomingOff.test.js). `now` pins the clock in tests.
export default function WhatsNewPill({ today: todayProp, variant = 'dateline', triggerClassName, openOnHover = false, upcoming = null, houseId = null, now: nowProp = null }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // last-seen as it stood when the sheet OPENED — rows keep their green tint
  // for the whole open even though the stamp advances immediately.
  const [lastSeenAtOpen, setLastSeenAtOpen] = useState(() => readLastSeen());
  const [seenStamp, setSeenStamp] = useState(() => readLastSeen());
  const rootRef = useRef(null);
  const hoverTimer = useRef(null);
  const pinned = useRef(false); // opened (or kept open) by a click: leaving never closes it
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const today = useMemo(() => todayProp || localToday(), [todayProp]);
  const entries = useMemo(() => WHATS_NEW_ENTRIES.slice(0, SHEET_ENTRY_LIMIT), []);
  // Scope groups, order preserved inside each; house leads.
  const houseEntries = entries.filter((e) => e.where === 'house');
  const everywhereEntries = entries.filter((e) => e.where !== 'house');

  // The whole dateline exists only while the newest entry is inside the
  // window (lastSeen: null = pure window+future check); the NEW label is
  // green only while genuinely unread.
  const inWindow = shouldShowBadge({
    entries: WHATS_NEW_ENTRIES, lastSeen: null, today, windowDays: BADGE_WINDOW_DAYS,
  });
  const unread = shouldShowBadge({
    entries: WHATS_NEW_ENTRIES, lastSeen: seenStamp, today, windowDays: BADGE_WINDOW_DAYS,
  });

  const newest = entries[0];

  // 🧭 10-03 explore: computed every render, so the words are fresh when the sheet opens
  const comingUp = upcomingRows(upcoming, { now: Number.isFinite(nowProp) ? nowProp : Date.now(), spaceId: houseId });
  const [eventsSeen, setEventsSeen] = useState(() => readSeenEvents(storageOf()));

  const show = () => {
    if (!open) {
      setLastSeenAtOpen(seenStamp);
      const newestDate = newestEntryDate(WHATS_NEW_ENTRIES);
      if (newestDate) { writeLastSeen(newestDate); setSeenStamp(newestDate); }
      if (comingUp.length) setEventsSeen(markEventsSeen(storageOf(), comingUp)); // 🧭 10-03 explore: the night's dot
    }
    setOpen(true);
  };
  const hide = () => { clearTimeout(hoverTimer.current); setOpen(false); };
  // however it closes (a click, Escape, outside, a footer door), the next open starts unpinned
  useEffect(() => { if (!open) pinned.current = false; }, [open]);
  const toggle = () => {
    clearTimeout(hoverTimer.current);
    if (open && !pinned.current && openOnHover) { pinned.current = true; return; } // hover opened it: the click keeps it
    if (open) { hide(); return; }
    pinned.current = true;
    show();
  };
  const hover = openOnHover ? {
    onMouseEnter: () => {
      if (!canHover()) return;
      clearTimeout(hoverTimer.current);
      if (!open) hoverTimer.current = setTimeout(show, HOVER_OPEN_MS);
    },
    onMouseLeave: () => {
      if (!canHover()) return;
      clearTimeout(hoverTimer.current);
      if (open && !pinned.current) hoverTimer.current = setTimeout(hide, HOVER_CLOSE_MS);
    },
  } : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pill = variant === 'pill';
  // 🧭 10-03 explore: a quiet week (no new entry in the window) keeps the PILL while nights
  // are coming; its sheet then shows only Coming up + the footer. The dateline keeps its rule.
  const hasComing = comingUp.length > 0;
  const quiet = !newest || !inWindow;
  if (quiet && !(pill && hasComing)) return null;
  const pillUnread = unread || (pill && eventDot(comingUp, eventsSeen));
  const upHouse = houseId ? spacePathFor(houseId) : HOUSE_SPACE_PATH;

  return (
    <div ref={rootRef} style={pill ? styles.rootPill : styles.root} {...hover}>
      {pill ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={triggerClassName}
          data-unread={pillUnread ? '1' : undefined}
        >
          {pillUnread && <span aria-hidden="true" />}
          What's new
        </button>
      ) : (
      /* the dateline — the whole line is the trigger */
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="What's new"
        style={styles.dateline}
      >
        <span style={{ ...styles.newLabel, ...(unread ? null : styles.newLabelSeen) }}>NEW</span>
        <span style={styles.datelineText}>{datelineFor(newest)}</span>
        {newest.command && <code style={styles.datelineCode}>{newest.command}</code>}
        <span style={styles.moreLink}>{open ? '· less' : '· more'}</span>
      </button>
      )}

      {open && (
        <div role="dialog" aria-label="What's new" style={pill ? styles.sheetPill : styles.sheet}>
          <div style={styles.sheetHeader}>
            <div style={styles.sheetHeaderLeft}>
              <span style={styles.sheetTitle}>What's new</span>
              {!quiet && <span style={styles.sheetSub}>since your last visit</span>}
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={styles.closeBtn}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                <path d="M2 2 L12 12 M12 2 L2 12" />
              </svg>
            </button>
          </div>

          {/* 🧭 10-03 explore: the house's next nights, on the house clock ("house time" once, here) */}
          {hasComing && (
            <>
              <div style={{ ...styles.scopeHeader, ...styles.upHeader }}>
                <span style={{ ...styles.scopeLabel, ...styles.upLabel }}>{'\u{1F5D3}\u{FE0F}'} COMING UP AT THE HOUSE · HOUSE TIME</span>
                <button
                  type="button"
                  style={{ ...styles.scopeDoor, ...styles.upDoor }}
                  onClick={() => { setOpen(false); navigate(upHouse); }}
                >
                  visit the house
                </button>
              </div>
              {comingUp.map((row, i) => (
                <UpcomingRow
                  key={row.key}
                  row={row}
                  isLast={i === comingUp.length - 1}
                  onGo={() => { setOpen(false); navigate(row.href); }}
                />
              ))}
            </>
          )}

          {!quiet && houseEntries.length > 0 && (
            <>
              <div style={styles.scopeHeader}>
                <span style={styles.scopeLabel}>{'\u{1F3E0}'} IN THE CLAUDE HOUSE</span>
                <button
                  type="button"
                  style={styles.scopeDoor}
                  onClick={() => { setOpen(false); navigate(HOUSE_SPACE_PATH); }}
                >
                  visit the house
                </button>
              </div>
              {houseEntries.map((entry, i) => (
                <Entry
                  key={entry.date + entry.title}
                  entry={entry}
                  unseen={entryIsUnseen(entry, lastSeenAtOpen)}
                  today={today}
                  isLast={i === houseEntries.length - 1 && everywhereEntries.length === 0}
                />
              ))}
            </>
          )}

          {!quiet && everywhereEntries.length > 0 && (
            <>
              {(houseEntries.length > 0 || hasComing) && (
                <div style={{ ...styles.scopeHeader, ...styles.scopeHeaderMuted }}>
                  <span style={{ ...styles.scopeLabel, ...styles.scopeLabelMuted }}>{'\u{1F30D}'} IN EVERY SPACE</span>
                </div>
              )}
              {everywhereEntries.map((entry, i) => (
                <Entry
                  key={entry.date + entry.title}
                  entry={entry}
                  unseen={entryIsUnseen(entry, lastSeenAtOpen)}
                  today={today}
                  isLast={i === everywhereEntries.length - 1}
                />
              ))}
            </>
          )}

          <div style={styles.sheetFooter}>
            <button type="button" style={styles.footerLink} onClick={() => { setOpen(false); navigate('/whats-new'); }}>
              See all updates
            </button>
            {/* 🐛 09-20: the one bug-report door outside a space (in a space it is `/bug`) */}
            <span style={styles.footerDot} aria-hidden="true">·</span>
            <button type="button" style={styles.footerLink} onClick={() => { setOpen(false); openBugReport({ source: 'whats_new' }); }}>
              🐛 Report a bug
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function datelineFor(entry) {
  return entry.datelineText || entry.title;
}

const styles = {
  root: { position: 'relative', display: 'block', marginTop: 9 },
  dateline: {
    display: 'inline-flex', alignItems: 'baseline', gap: 7, maxWidth: '100%',
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: '0.78rem', color: '#666',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  newLabel: {
    color: '#43a047', fontWeight: 700, letterSpacing: '0.08em',
    fontSize: '0.68rem', flexShrink: 0,
  },
  newLabelSeen: { color: '#aaa' },
  datelineText: { overflow: 'hidden', textOverflow: 'ellipsis' },
  datelineCode: {
    fontFamily: "'SF Mono', Menlo, monospace", fontSize: '0.74rem',
    color: '#7b1fa2', flexShrink: 0,
  },
  moreLink: { color: '#999', flexShrink: 0 },
  sheet: {
    position: 'absolute', top: 26, left: 0, zIndex: 5000,
    width: 'min(396px, calc(100vw - 40px))', boxSizing: 'border-box',
    background: '#fff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 14,
    boxShadow: '0 14px 44px rgba(30,10,50,0.16)', overflow: 'hidden',
    textAlign: 'left',
  },
  sheetHeader: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '14px 18px 10px',
  },
  rootPill: { position: 'relative', display: 'inline-block' },
  sheetPill: {
    position: 'absolute', top: 42, right: 0, zIndex: 5000,
    width: 'min(396px, calc(100vw - 40px))', boxSizing: 'border-box',
    background: '#fff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 14,
    boxShadow: '0 14px 44px rgba(30,10,50,0.16)', overflow: 'hidden',
    textAlign: 'left',
  },
  sheetHeaderLeft: { display: 'flex', alignItems: 'baseline', gap: 8 },
  sheetTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#111' },
  sheetSub: { fontSize: '0.7rem', color: '#999', fontWeight: 400 },
  closeBtn: {
    background: 'none', border: 'none', padding: 4, margin: -4, cursor: 'pointer',
    color: '#999', display: 'inline-flex',
  },
  scopeHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 18px 6px', background: '#f3eef8',
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  scopeHeaderMuted: { background: 'rgba(0,0,0,0.025)' },
  scopeLabel: {
    fontSize: '0.62rem', fontWeight: 700, color: '#7b5a96', letterSpacing: '0.12em',
  },
  scopeLabelMuted: { color: '#8a8a8a' },
  scopeDoor: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: '0.64rem', fontWeight: 600, color: '#7b1fa2',
  },
  entry: { display: 'flex', gap: 12, padding: '13px 18px', boxSizing: 'border-box' },
  entryDivider: { borderBottom: '1px solid rgba(0,0,0,0.05)' },
  entryUnseen: { background: 'rgba(67,160,71,0.045)' },
  entryEmoji: {
    width: 34, height: 34, borderRadius: 10, background: '#f3eef8', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem',
  },
  entryBody: { flex: 1, minWidth: 0 },
  entryTopRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 },
  entryTitle: { fontSize: '0.84rem', fontWeight: 700, color: '#111' },
  entryDate: { fontSize: '0.62rem', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', flexShrink: 0 },
  entryDateToday: { color: '#43a047', fontWeight: 700 },
  entryText: { fontSize: '0.78rem', color: '#555', lineHeight: 1.45, marginTop: 2 },
  chipRow: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 7 },
  chip: {
    fontFamily: "'SF Mono', Menlo, monospace", fontSize: '0.74rem', color: '#7b1fa2',
    background: '#f3eef8', border: '1px solid rgba(123,31,162,0.25)', borderRadius: 6,
    padding: '3px 8px', cursor: 'pointer',
  },
  chipHint: { fontSize: '0.68rem', color: '#999' },
  sheetFooter: {
    padding: '10px 18px 12px', borderTop: '1px solid rgba(0,0,0,0.06)', textAlign: 'center',
  },
  footerDot: { color: '#bbb', margin: '0 8px', fontSize: '0.74rem' },
  footerLink: {
    background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '0.74rem', fontWeight: 600, color: '#7b1fa2',
  },
  // 🧭 10-03 explore: the warm "Coming up" group (look C's dark mode inverts the sheet:
  // these read as a dark brown band with gold words there, the mockup's look)
  // the header stays ONE line from a 390 phone up; narrower, its door drops under it (right)
  upHeader: { gap: '2px 8px', flexWrap: 'wrap', background: '#faf1e3' },
  upLabel: { color: '#8a5a14', letterSpacing: '0.1em' },
  upDoor: { color: '#9a5b06', flexShrink: 0, whiteSpace: 'nowrap', marginLeft: 'auto' },
  upRow: {
    width: '100%', alignItems: 'center', textAlign: 'left', cursor: 'pointer', background: 'none',
    borderTop: 'none', borderLeft: 'none', borderRight: 'none', margin: 0, fontFamily: 'inherit', color: 'inherit',
  },
  upEmoji: { background: '#f7ecdb' },
  upTitle: { display: 'block', fontSize: '0.84rem', fontWeight: 700, color: '#111', overflowWrap: 'anywhere' },
  upWhen: { display: 'block', fontSize: '0.78rem', color: '#555', lineHeight: 1.45, marginTop: 2 },
  upWhenLive: { color: '#c62828', fontWeight: 600 },
  upLiveDot: {
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#e53935',
    marginRight: 6, verticalAlign: 'middle', position: 'relative', top: -1,
  },
  upBy: { color: '#777', fontWeight: 400 },
  upTag: {
    display: 'inline-block', marginLeft: 6, padding: '0 7px', borderRadius: 999, border: '1px solid rgba(154,91,6,0.45)',
    color: '#9a5b06', fontSize: '0.66rem', fontWeight: 700, lineHeight: '16px', verticalAlign: 'middle',
  },
};
